import {
  isProductionMode,
  ASSETS_FROM_API,
  ASSETS_URL,
  STATIC_ASSETS_URL,
} from "./consts"
import {
  assetsHostFor,
  isAssetPath,
  rehostSavedUrl,
  sectionLogoUrl,
} from "./assetsHost"
import { assetsUrlFromApi } from "./assetsPath"

export const getFullImageUrl = (url: string) => {
  const sectionLogo = sectionLogoUrl(url)
  if (sectionLogo) return sectionLogo

  const fromApi = assetsUrlFromApi(url)
  if (fromApi) return fromApi

  let baseUrl = "https://app-stag.babele.co"

  if (ASSETS_FROM_API) {
    baseUrl = ASSETS_URL
  } else if (import.meta.env.VITE_PROD_NAME === "ms") {
    if (
      !url.startsWith("http") &&
      !url.startsWith("/static") &&
      url.toLocaleLowerCase().includes("uploads")
    ) {
      baseUrl = "https://assets.babele.co"
    }
  } else {
    if (isProductionMode()) {
      baseUrl = "https://assets-prod.babele.co"
    }
  }

  const rehosted = rehostSavedUrl(url)
  if (rehosted) return rehosted

  if (url.startsWith("http")) return url

  if (url.startsWith("/Images")) {
    return `${baseUrl}${url}`
  }

  if (url.startsWith("Images")) {
    return `${baseUrl}/${url}`
  }

  if (url.startsWith("\\Images")) {
    return `${baseUrl}/${url.replace(/\\/g, "/").replace(/^\/+/, "")}`
  }

  // Other /assets/ paths are bundled with the web client and load as they are.
  if (isAssetPath(url)) {
    return `${assetsHostFor(url)}${url.startsWith("/") ? "" : "/"}${url}`
  }

  // Files the old app shipped under /static, still served by the static assets host.
  if (url.startsWith("/static/")) {
    return `${STATIC_ASSETS_URL}${url}`
  }

  return url
}

export const fixImageUrlsInHtml = (html: string): string => {
  if (!html || !html.includes("img")) return html
  const wrapper = document.createElement("div")
  wrapper.innerHTML = html
  const imgs = wrapper.getElementsByTagName("img")
  for (let i = 0; i < imgs.length; i++) {
    const img = imgs[i]
    const src = img.getAttribute("src")
    if (src) {
      img.setAttribute("src", getFullImageUrl(src))
    }
  }
  return wrapper.innerHTML
}

export type PixelCrop = {
  x: number
  y: number
  width: number
  height: number
}

export const cropImageToBlob = (
  imageSrc: string,
  pixelCrop: PixelCrop,
  fileName: string,
  fileType: string,
): Promise<File> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = "anonymous"
    image.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = pixelCrop.width
      canvas.height = pixelCrop.height
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        reject(new Error("Canvas context not available"))
        return
      }

      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const sx = Math.max(0, pixelCrop.x)
      const sy = Math.max(0, pixelCrop.y)
      const sw = Math.min(pixelCrop.width, image.naturalWidth - sx)
      const sh = Math.min(pixelCrop.height, image.naturalHeight - sy)

      if (sw > 0 && sh > 0) {
        const dx = sx - pixelCrop.x
        const dy = sy - pixelCrop.y
        ctx.drawImage(image, sx, sy, sw, sh, dx, dy, sw, sh)
      }

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Failed to convert canvas to blob"))
          return
        }
        resolve(new File([blob], fileName, { type: fileType }))
      }, fileType)
    }
    image.onerror = () => reject(new Error("Failed to load image for cropping"))
    image.src = imageSrc
  })

export const getInitials = (name: string): string => {
  if (!name || !name.trim()) {
    return ""
  }

  const nameParts = name
    .trim()
    .split(" ")
    ?.filter((str) => str !== "")

  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase()
  }
  return (
    nameParts[0].charAt(0).toUpperCase() + nameParts[1].charAt(0).toUpperCase()
  )
}
